import { RoundedBox } from '@react-three/drei'
import { useMemo } from 'react'
import { comprimento, normalizar, type Vetor3 } from '../../dominio/geometria/vetor3'
import { COMPRIMENTO_PRANCHA } from '../../dominio/didatico/rotasExtracao'
import type { ConfiguracaoVeiculo } from '../../dominio/didatico/configuracaoVeiculo'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'

/** Nome fixo: só há uma prancha ativa por vez, e "alinhamento vítima-prancha" da câmera precisa achá-la. */
export const NOME_PRANCHA_ATIVA = `${PREFIXO_SELECIONAVEL}prancha-ativa`

interface Props {
  /** origem da rota: onde a prancha encosta na vítima */
  base: Vetor3
  /** destino da rota: para onde a prancha aponta */
  pontoSaida: Vetor3
  /** altura do tabuleiro */
  altura: number
  /** 0 = fora do veículo, 1 = posicionada sob a vítima */
  fracaoEntrada: number
  /** deslocamento da vítima: depois de deitada, a prancha sai junto com ela */
  deslocamento: Vetor3
  configuracao: ConfiguracaoVeiculo
  /** eixo longitudinal do corpo, usado para inserir a prancha sob quem já está deitado */
  direcaoCorpo: Vetor3
}

const LARGURA = 0.45
const ESPESSURA = 0.05
/**
 * Centro da prancha ao longo do eixo longitudinal do corpo (+d aponta para a cabeça).
 *
 * Com `base` na base da coluna (L5/S1), a cabeça termina a +0,78 m e os pés
 * a -0,91 m. Com a prancha (1,85 m) centrada a -0,05 m de `base`, o topo fica a
 * +0,875 m (+9,5 cm além da cabeça) e o fundo a -0,975 m (-6,5 cm além dos pés),
 * mantendo o corpo inteiro apoiado sobre o tabuleiro da cabeça aos pés.
 */
const AVANCO_CENTRO = -0.05

/**
 * Prancha longa de imobilização.
 *
 * Entra antes da vítima sair e fica parada, ponte entre o banco e o exterior:
 * é a vítima que desliza sobre ela. É a sequência que a equipe executa, e é o
 * que o instrutor pediu para a maquete mostrar.
 */
export function Prancha({
  base,
  pontoSaida,
  altura,
  fracaoEntrada,
  deslocamento,
  configuracao,
  direcaoCorpo,
}: Props) {
  const { posicao, guinada, tamanho } = useMemo(() => {
    const horizontal = { x: direcaoCorpo.x, y: 0, z: direcaoCorpo.z }
    const d = comprimento(horizontal) < 1e-6 ? { x: 0, y: 0, z: -1 } : normalizar(horizontal)
    // uma prancha real tem comprimento fixo de fábrica (1,85 m)
    const tamanho = COMPRIMENTO_PRANCHA
    // centro da prancha posicionado para apoiar a vítima da cabeça aos pés
    const avanco = AVANCO_CENTRO
    return {
      tamanho,
      guinada: Math.atan2(d.x, d.z),
      posicao: [
        base.x + d.x * avanco + (configuracao === 'lateral' ? -0.04 : 0),
        configuracao === 'invertido' ? base.y + 0.04 : configuracao === 'lateral' ? base.y : altura,
        base.z + d.z * avanco,
      ] as [number, number, number],
    }
  }, [
    direcaoCorpo.x,
    direcaoCorpo.z,
    base.x,
    base.y,
    base.z,
    altura,
    configuracao,
  ])

  if (fracaoEntrada <= 0.01) return null

  // No veículo capotado ou tombado, a retirada é feita exclusivamente pela tampa traseira
  // com a técnica da pá de forno: a prancha entra deslizando da traseira (-Z) para a frente (+Z).
  // No veículo sobre as rodas (4 rodas), a prancha deve sempre entrar no ângulo de saída
  // (seja 90°, 60°, 30° ou 0°/traseira), vindo de fora da abertura em direção à vítima.
  const entraPelaTraseira = configuracao === 'invertido' || configuracao === 'lateral'
  const recuoEntrada = (1 - fracaoEntrada) * tamanho * 0.9
  const dx = entraPelaTraseira ? 0 : Math.sin(guinada) * recuoEntrada
  const dz = entraPelaTraseira ? -recuoEntrada : Math.cos(guinada) * recuoEntrada

  return (
    <group
      position={[
        posicao[0] + dx + deslocamento.x,
        posicao[1] + deslocamento.y,
        posicao[2] + dz + deslocamento.z,
      ]}
      rotation={[0, entraPelaTraseira ? 0 : guinada, configuracao === 'lateral' ? Math.PI / 2 : 0]}
      name={NOME_PRANCHA_ATIVA}
    >
      <RoundedBox args={[LARGURA, ESPESSURA, tamanho]} radius={0.02} smoothness={3}>
        <meshStandardMaterial color="#e8b33a" roughness={0.55} metalness={0.05} />
      </RoundedBox>
      {/* pegas laterais, para a peça ler como prancha e não como tábua */}
      {[-1, 1].map((lado) =>
        [-0.28, 0, 0.28].map((frac) => (
          <mesh
            key={`${lado}:${frac}`}
            position={[(lado * LARGURA) / 2, 0, frac * tamanho]}
            rotation={[0, 0, Math.PI / 2]}
          >
            <torusGeometry args={[0.045, 0.012, 6, 12, Math.PI]} />
            <meshStandardMaterial color="#b8862a" roughness={0.6} />
          </mesh>
        )),
      )}
    </group>
  )
}
